import React, { useState } from 'react';
import { Box, Typography, Button, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Sort as SortIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NewNoteDialog from './NewNoteDialog';
import { useUpdatePropertyMutation } from '../store/docApi';
import { generateEntityId, getCosmosDate, formatDateTime } from '../utils';

export default function Notes({ connectionIndex, project, currentStepId, currentStep }) {
  const [isNewNoteOpen, setIsNewNoteOpen] = useState(false);
  const [sortBy, setSortBy] = useState('date-desc');
  const [updateProperty] = useUpdatePropertyMutation();
  const navigate = useNavigate();

  const notes = project?.connections?.[connectionIndex]?.notes || [];
  const sortedNotes = [...notes].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return (a.name || '').localeCompare(b.name || '');
      case 'name-desc':
        return (b.name || '').localeCompare(a.name || '');
      case 'date-asc':
        return new Date(a.startDate) - new Date(b.startDate);
      case 'date-desc':
      default:
        return new Date(b.startDate) - new Date(a.startDate);
    }
  });

  const handleNewNoteClose = () => {
    setIsNewNoteOpen(false);
  };

  const handleNoteClick = (note) => {
    navigate(`/projects/${project.pk}/${project.id}/${connectionIndex}/notes/${note.id}`);
  };

  const getStepName = () => {
    if (!currentStep) return '';
    return currentStep.title || currentStep.name || `Step ${currentStepId}`;
  };

  async function handleAddNote(name, text, stepIds, startDate) {
    const id = generateEntityId('note');
    let updateParams = {
      label: 'project',
      pk: project.pk,
      id: project.id,
      name: `connections/${connectionIndex}/notes/-`,
      value: {
        id: id,
        name: name || 'Untitled Note',
        text: text || '',
        stepIds: Array.isArray(stepIds) ? stepIds : [],
        startDate: startDate || getCosmosDate()
      }
    };
    await updateProperty(updateParams);
  }

  const renderNote = (note) => {
    if (!currentStepId || (note.stepIds && note.stepIds.includes(currentStepId))) {
      return (
        <Box
          key={note.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
          onClick={() => handleNoteClick(note)}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {note.name || 'Untitled Note'}
            </Typography>
            {note.description && <Typography variant="body1" sx={{ mb: 1 }}>
               {note.description}
            </Typography>}
            {note.text && <Typography variant="body2" sx={{ mb: 1 }}>
               {note.text.substring(0, 50) + '...'}
            </Typography>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {Array.isArray(note.stepIds) && note.stepIds.map((stepId) => (
                <Chip
                  key={stepId}
                  label={`Step ${stepId}`}
                  size="small"
                  variant="outlined"
                  color={stepId === currentStepId ? "primary" : "default"}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              <div style={{ 
                display: 'flex', 
                flexDirection: 'row', 
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                <span>{note.startDate && formatDateTime(note.startDate)}</span>
                <span style={{ color: 'text.disabled' }}>{note.id}</span>
              </div>
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => setIsNewNoteOpen(true)}
        >
          Add Note
        </Button>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="sort-notes-label">Sort by</InputLabel>
          <Select
            labelId="sort-notes-label"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label="Sort by"
            startAdornment={<SortIcon sx={{ color: 'action.active', mr: 1 }} />}
          >
            <MenuItem value="date-desc">Date (Newest First)</MenuItem>
            <MenuItem value="date-asc">Date (Oldest First)</MenuItem>
            <MenuItem value="name-asc">Name (A-Z)</MenuItem>
            <MenuItem value="name-desc">Name (Z-A)</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {currentStepId && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Showing notes for Step {currentStepId}
        </Typography>
      )}
      <Box>
        {sortedNotes.map(renderNote)}
      </Box>

      <NewNoteDialog
        open={isNewNoteOpen}
        onClose={handleNewNoteClose}
        project={project}
        connectionIndex={connectionIndex}
        currentStepId={currentStepId}
        defaultName={getStepName()}
        onAddNote={handleAddNote}
      />
    </Box>
  );
} 